import { useState } from 'react';
import { Link } from 'react-router-dom';
import { signupShopAdmin } from '../api/signup';

const BENEFITS = [
  {
    title: 'Your own storefront',
    description:
      'Launch instantly on a salesy.store address — a professional home for your store from day one.',
  },
  {
    title: 'Connect your own domain',
    description:
      'Use your brand on the web. Point shop.yourbrand.com to your store with a simple DNS setup.',
  },
  {
    title: 'Run your business in one place',
    description: 'Products, orders, inventory, and customers — managed from a single dashboard.',
  },
  {
    title: 'Built to scale with you',
    description: 'Start solo, invite your team later, and grow without switching platforms.',
  },
];

const emptyForm = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  confirm_password: '',
};

export default function Landing() {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (form.password !== form.confirm_password) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await signupShopAdmin({
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        password: form.password,
      });
      window.location.href = '/shop';
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="landing">
      <div className="landing-inner">
        <section className="landing-hero" aria-label="About SalesBotics">
          <p className="landing-eyebrow">Commerce platform for founders</p>
          <h1 className="landing-headline">
            Everything you need to
            <span className="landing-headline-accent"> sell online</span>
          </h1>
          <p className="landing-lead">
            Launch your store, manage orders, and reach customers — from one platform
            built for entrepreneurs. Start on salesy.store, then connect your own domain
            when you&apos;re ready to put your brand front and center.
          </p>

          <ul className="landing-benefits">
            {BENEFITS.map((item) => (
              <li key={item.title}>
                <span className="landing-benefit-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="signup-panel" aria-label="Create account">
          <div className="signup-panel-header">
            <p className="signup-brand">SalesBotics</p>
            <h2>Start your store</h2>
            <p>Create your account in under a minute. You&apos;ll set up your storefront right after signing in.</p>
          </div>

          {error && <div className="signup-error">{error}</div>}

          <form className="signup-form" onSubmit={handleSubmit}>
            <div className="form-grid two-col">
              <div className="form-field">
                <label htmlFor="signup-first">First name</label>
                <input
                  id="signup-first"
                  value={form.first_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, first_name: event.target.value }))}
                  required
                  autoComplete="given-name"
                  placeholder="Jane"
                />
              </div>
              <div className="form-field">
                <label htmlFor="signup-last">Last name</label>
                <input
                  id="signup-last"
                  value={form.last_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, last_name: event.target.value }))}
                  required
                  autoComplete="family-name"
                  placeholder="Smith"
                />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="signup-email">Work email</label>
              <input
                id="signup-email"
                type="email"
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                required
                autoComplete="email"
                placeholder="you@company.com"
              />
            </div>

            <div className="form-grid two-col">
              <div className="form-field">
                <label htmlFor="signup-password">Password</label>
                <input
                  id="signup-password"
                  type="password"
                  value={form.password}
                  onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="8+ characters"
                />
              </div>
              <div className="form-field">
                <label htmlFor="signup-confirm">Confirm</label>
                <input
                  id="signup-confirm"
                  type="password"
                  value={form.confirm_password}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, confirm_password: event.target.value }))
                  }
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder="Repeat password"
                />
              </div>
            </div>

            <button type="submit" className="btn btn-primary signup-submit" disabled={submitting}>
              {submitting ? 'Creating your account…' : 'Create free account'}
            </button>
          </form>

          <p className="signup-footer">
            Already have an account? <Link to="/shop">Sign in</Link>
          </p>
        </section>
      </div>
    </div>
  );
}
